import MuiAutocomplete from '@mui/material/Autocomplete';
import MuiTextField from '@mui/material/TextField';

import {
  useSupportEnterFacility,
  useSupportFacilityOptions,
} from '@sbv-clients/data-access-api';
import { useAuth } from '@sbv-clients/shared-feature-auth';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@sbv-clients/ui-shared';

export const FacilityPicker = () => {
  const { data: facilities, isLoading, isError } = useSupportFacilityOptions();
  const { mutateAsync: enterFacility, isLoading: isEntering } =
    useSupportEnterFacility();
  const { onAuthSuccess } = useAuth();

  const options = facilities ?? [];

  const handleChange = async (
    _: unknown,
    facility: (typeof options)[number] | null
  ) => {
    if (!facility?.id) return;
    try {
      const result = await enterFacility(facility.id);
      if (result?.token && result?.refreshToken) {
        onAuthSuccess(result.token, result.refreshToken);
      }
    } catch {
      // error is handled by useSupportEnterFacility's onError (notifyError snackbar)
    }
  };

  return (
    <Box
      height="100%"
      display="flex"
      flexDirection="column"
      alignItems="center"
      px={2}
      pt={6}
    >
      <Box width="100%" maxWidth={480}>
        <Typography
          sx={{ fontSize: 20, fontWeight: 700, color: 'text.primary', mb: 3 }}
        >
          Select a Facility
        </Typography>

        {isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load facilities. Please refresh and try again.
          </Alert>
        )}

        <MuiAutocomplete
          options={options}
          getOptionLabel={(o) => o.name ?? ''}
          loading={isLoading}
          disabled={isEntering}
          onChange={handleChange}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.id}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
              >
                <Typography variant="body2">{option.name}</Typography>
                {option.usingSbvPro && (
                  <Chip
                    label="Pro"
                    size="small"
                    color="primary"
                    sx={{ ml: 1 }}
                  />
                )}
              </Stack>
            </Box>
          )}
          renderInput={(params) => (
            <MuiTextField
              {...params}
              placeholder="Search facilities…"
              size="small"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading || isEntering ? (
                      <CircularProgress size={16} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </Box>
    </Box>
  );
};
